/**
 * 자동 동기화 와이어링(결정 #20: 디바운스 자동 동기화). core의 플랫폼 중립
 * {@link AutoSyncController}(타이밍 정책)에 web의 부품을 주입한다:
 *   - 변경 감지: Dexie liveQuery로 동기화 대상 테이블의 변동을 관찰 → notifyChange.
 *   - 실행:      syncWithGoogleDrive(설정의 충돌 전략).
 *   - 게이트:    설정(sync.enabled && sync.auto) + Google 연결 여부.
 *
 * 무한 에코 방지(매우 중요): 동기화의 pull 단계는 applyRecords로 IndexedDB에 쓰므로
 * 그 쓰기가 다시 liveQuery를 깨워 notifyChange를 부른다. 컨트롤러의 인플라이트 가드 +
 * 에코 억제 창이 이를 흡수한다(자세한 근거는 core sync/auto.ts). 그래서 **설정 테이블은
 * 변경 신호에서 제외**한다 — 사이클 끝의 lastSyncedAt 쓰기가 또 다른 에코가 되지
 * 않도록. 설정 변경(토글/디바운스)은 별도 구독으로 컨트롤러 구성에만 반영한다.
 */
import { AutoSyncController } from "@roqeet/core";
import { liveQuery, type Subscription } from "dexie";
import { getDB } from "../platform/db/schema.js";
import { storage } from "./storage.js";
import { identity } from "./identity.js";
import { syncWithGoogleDrive } from "./sync-actions.js";
import type { SyncConflict, ConflictResolution } from "@roqeet/core";

/** 변경 신호 대상 테이블(설정 제외 — 에코 방지). web-sync-local의 SYNC_TABLES와 정렬. */
const OBSERVED_TABLES = [
  "characters",
  "personas",
  "chats",
  "lorebooks",
  "presets",
  "scripts",
  "providerProfiles",
  "generatedImages",
  "assets",
  "messages",
  "gameCards",
] as const;

/** 자동 동기화용 충돌 해소 전략: 사용자 개입 없이 보수적으로 내 변경(keep-local) 우선 적용 */
const autoResolveConflicts = async (conflicts: SyncConflict[]): Promise<Map<string, ConflictResolution>> => {
  return new Map(conflicts.map((c) => [c.id, "keep-local" as ConflictResolution]));
};

let controller: AutoSyncController | null = null;
let changeSub: Subscription | null = null;
let settingsSub: Subscription | null = null;
let lastSettledListener: ((ok: boolean) => void) | null = null;

/**
 * 지금 자동 동기화를 돌려도 되는지. 매 사이클 직전에 평가되어 설정 변경이 즉시 반영된다.
 * 토큰 유효성은 여기서 보지 않는다(네트워크 회피) — 무효 토큰이면 syncWithGoogleDrive가
 * throw하고 컨트롤러의 onError가 흡수한다.
 */
async function isEnabled(): Promise<boolean> {
  try {
    const settings = await storage.getSettings();
    if (!settings?.sync.enabled || !settings.sync.auto) return false;
    if (!identity.isConfigured()) return false;
    return identity.current().tier === "google";
  } catch {
    return false;
  }
}

/**
 * 자동 동기화를 초기화하고 관찰을 시작한다(부팅 시 1회). 이미 초기화됐으면 무시한다.
 * Google 미설정/미연결이어도 안전하다 — isEnabled가 false라 실제로는 돌지 않고,
 * 연결되는 순간부터 다음 변경에 반응한다.
 *
 * @param onSettled 사이클 종료 콜백(옵션). UI가 "마지막 동기화"를 갱신할 때 쓴다.
 */
export async function initAutoSync(onSettled?: (ok: boolean) => void): Promise<void> {
  if (controller) {
    // 재초기화: settled 리스너만 교체(중복 구독 방지).
    lastSettledListener = onSettled ?? null;
    return;
  }
  lastSettledListener = onSettled ?? null;

  const settings = await storage.getSettings();
  const debounceMs = settings?.sync.debounceMs ?? 10000;

  controller = new AutoSyncController({
    run: () => syncWithGoogleDrive(autoResolveConflicts),
    isEnabled,
    debounceMs,
    // 자동 동기화는 사용자 알림 없이 조용히 실패해야 한다(루프를 죽이지 않음).
    onError: (e) => console.warn("[auto-sync] 동기화 실패(자동, 무시됨):", e),
    onSettled: (ok) => lastSettledListener?.(ok),
  });
  controller.start();

  // 1) 데이터 변경 관찰 → notifyChange. 전체 테이블 count를 읽어 add/update/delete
  //    어느 것이든(같은 카운트의 in-place 수정 포함) 해당 테이블 변동에 재발화한다.
  const db = getDB();
  changeSub = liveQuery(async () => {
    let sum = 0;
    for (const name of OBSERVED_TABLES) {
      sum += await db.table(name).count();
    }
    return { sum, _tick: Date.now() }; // count가 동일한 update 상황에서도 이벤트를 방출하도록 강제
  }).subscribe({
    next: () => controller?.notifyChange(),
    error: (e) => console.warn("[auto-sync] 변경 관찰 오류:", e),
  });

  // 2) 설정 변경 관찰 → 디바운스/활성 상태를 컨트롤러 구성에 반영(변경 신호 아님).
  settingsSub = liveQuery(() => db.settings.get("global")).subscribe({
    next: (s) => {
      if (s?.sync.debounceMs) controller?.setDebounceMs(s.sync.debounceMs);
    },
    error: (e) => console.warn("[auto-sync] 설정 관찰 오류:", e),
  });
}

/**
 * 즉시 한 번 동기화를 시도한다(디바운스 생략, 설정/연결 확인은 거침). 사용자가 자동
 * 동기화를 막 켰을 때 "지금 한 번 맞추기" 용도. 컨트롤러 미초기화면 무시.
 */
export async function triggerAutoSyncNow(): Promise<void> {
  await controller?.triggerNow();
}

/** 관찰을 멈추고 구독을 해제한다(테스트·정리용). */
export function stopAutoSync(): void {
  controller?.stop();
  changeSub?.unsubscribe();
  settingsSub?.unsubscribe();
  changeSub = null;
  settingsSub = null;
  controller = null;
  lastSettledListener = null;
}
